import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, X } from "lucide-react";

/**
 * Small multi-select (checkbox dropdown) styled like the app's inputs.
 * options: [{ value, label, color? }]; value: array of selected values.
 */
const MultiSelect = ({
  options = [],
  value = [],
  onChange,
  disabled = false,
  placeholder = "Select…",
  ariaLabel,
  // Optional custom trigger: ({ open, toggle, selected, disabled }) => node.
  renderTrigger,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const root = useRef(null);
  const panel = useRef(null);
  const [pos, setPos] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (root.current?.contains(e.target) || panel.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  // The panel is portalled to <body> (position: fixed) so a modal's scroll
  // container can't clip it; it opens upward when there is no room below.
  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      if (!root.current) return;
      const r = root.current.getBoundingClientRect();
      const below = window.innerHeight - r.bottom;
      const up = below < 280 && r.top > below;
      setPos({
        left: r.left,
        width: renderTrigger ? 256 : r.width,
        ...(up
          ? { bottom: window.innerHeight - r.top + 6, maxHeight: Math.max(120, r.top - 16) }
          : { top: r.bottom + 6, maxHeight: Math.max(120, below - 16) }),
      });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, renderTrigger]);

  const selected = useMemo(() => options.filter((o) => value.includes(o.value)), [options, value]);
  const visible = options.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase()));

  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);

  return (
    <div ref={root} className={renderTrigger ? "relative inline-block" : "relative"}>
      {renderTrigger ? (
        renderTrigger({ open, toggle: () => !disabled && setOpen((o) => !o), selected, disabled })
      ) : (
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        className={`input-field w-full min-h-[42px] flex items-center gap-1.5 flex-wrap text-left ${
          disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
        }`}
      >
        {selected.length === 0 ? (
          <span className="text-gray-400 dark:text-light-500">{placeholder}</span>
        ) : (
          selected.map((o) => (
            <span
              key={o.value}
              className="inline-flex items-center gap-1 max-w-full text-xs px-2 py-0.5 rounded-full bg-raise text-ink"
            >
              {o.color && <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: o.color }} />}
              <span className="truncate">{o.label}</span>
              {!disabled && (
                <span
                  role="button"
                  tabIndex={-1}
                  aria-label={`Remove ${o.label}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(o.value);
                  }}
                  className="hover:text-red-600"
                >
                  <X className="w-3 h-3" />
                </span>
              )}
            </span>
          ))
        )}
        <ChevronDown className="w-4 h-4 ml-auto shrink-0 text-faint" />
      </button>
      )}

      {open && !disabled && pos && createPortal(
        <div
          ref={panel}
          style={{ position: "fixed", zIndex: 100000, ...pos }}
          className="flex flex-col rounded-2xl border border-line2 bg-panel-solid shadow-modal overflow-hidden"
        >
          {options.length > 6 && (
            <div className="p-2 border-b border-line">
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                className="input-field !py-1.5 text-sm w-full"
                aria-label="Search options"
              />
            </div>
          )}
          <ul role="listbox" aria-multiselectable="true" className="max-h-56 min-h-0 overflow-y-auto py-1">
            {visible.length === 0 ? (
              <li className="px-3 py-2 text-sm text-gray-400">No options</li>
            ) : (
              visible.map((o) => {
                const on = value.includes(o.value);
                return (
                  <li key={o.value} role="option" aria-selected={on}>
                    <button
                      type="button"
                      onClick={() => toggle(o.value)}
                      className="flex w-full items-center gap-2 px-3 py-2 text-sm text-left text-ink hover:bg-hover"
                    >
                      <span
                        className={`w-4 h-4 shrink-0 rounded border flex items-center justify-center ${
                          on ? "bg-grad border-transparent text-on-accent" : "border-line2"
                        }`}
                      >
                        {on && <Check className="w-3 h-3" />}
                      </span>
                      {o.color && <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: o.color }} />}
                      <span className="truncate">{o.label}</span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default MultiSelect;
